<template>
  <MainLayout>
    <div class="fill-height overflow-xs-auto d-flex flex-xs-column pa-2">
      <div class="mx-1 utility-viewer-container">
        <!-- 3D Map Selector - Overlaid -->
        <div class="map-type-selector-overlay">
          <v-select v-model="selected3DMapOption" :items="map3DOptions" item-text="name" item-value="url" label="3D Map"
            prepend-inner-icon="3d_rotation" dense outlined hide-details clearable placeholder="Select a 3D Map"
            @change="open3DMapInNewTab"></v-select>
        </div>

        <Map ref="map">
          <ZoomControl position="topright" />
          <HomeControl position="topright" />

          <ScaleBarControl position="bottomleft" />

          <LayersControl position="bottomleft" legends-control-position="topright" />

          <UtilityViewerModule :current-utility="currentUtility" :utilities="utilities"
            :visible-overlay-bounds="visibleOverlayBounds" position="topright" />
        </Map>
      </div>

      <div class="d-flex flex-column mx-1 right-panel-container">
        <!-- Module Selection View (Default) -->
        <div v-if="!selectedModule" class="module-selection-container">
          <div class="module-selection-title">PROCESSING CATEGORIES</div>
          <div class="module-cards-grid">
            <div class="module-card" v-for="module in availableModules" :key="module.id"
              @click="selectModule(module.id)">
              <div class="module-card-icon" :style="{ backgroundColor: module.color }">
                <v-icon color="white">{{ module.icon }}</v-icon>
              </div>
              <div class="module-card-content">
                <div class="module-card-title">{{ module.title }}</div>
                <div class="module-card-description">{{ module.description }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Selected Module Content -->
        <template v-else>
          <!-- Back Button -->
          <!-- Module Content -->
          <div class="module-content flex">
            <div class="module-header">
              <v-btn icon small color="primary" @click="onQueryResetModule" class="back-button">
                <v-icon>arrow_back</v-icon>
              </v-btn>
              <span class="module-title">{{ getSelectedModuleTitle }}</span>
            </div>
            <TrackComplaintStatus v-if="selectedModule === 'trackComplaint'" class="flex-none" />

            <UtilityControl v-if="selectedModule === 'utilityControl'" v-model="utilities"
              :current-utility.sync="currentUtility" :visible-overlay-bounds="visibleOverlayBounds" style="width: 100%"
              class="flex" @pan-to-point="onPanToPoint" />

            <UtilitiesPayment v-if="selectedModule === 'utilitiesPayment'" class="flex-none" />
            <UtilityQuery v-if="selectedModule === 'utilityQuery'" class="flex" style="width: 100%"
              :visible-layer-names="visibleLayerNames"
              @pan-to-point="onPanToPoint" @apply-query="onApplyQuery" @reset="onQueryReset" @pan-to-aoi="onPanToAoi" />

          </div>
        </template>
      </div>
    </div>
  </MainLayout>
</template>

<script>
import TrackComplaintStatus from "./TrackComplaintStatus";
import UtilitiesPayment from "./UtilitiesPayment";

import UtilityViewerModule from "@/components/UtilityPoint/UtilityViewerModule";
import UtilityControl from "@/components/UtilityPoint/UtilityControl";

import Map from "@/components/Map/Map";
import ZoomControl from "@/components/Map/modules/ZoomControl/ZoomControl";
import LayersControl from "@/components/Map/modules/LayersControl/LayersControl";
import HomeControl from "@/components/Map/modules/HomeControl/HomeControl";
import ScaleBarControl from "@/components/Map/modules/ScaleBarControl/ScaleBarControl";
import UtilityQuery from "./UtilityQuery.vue";

function parseBounds(boundsArray) {
  const coords = Array.isArray(boundsArray[0][0]) ? boundsArray[0] : boundsArray;
  const lats = coords.map(c => c[0]);
  const lngs = coords.map(c => c[1]);
  return {
    south: Math.min(...lats),
    north: Math.max(...lats),
    west: Math.min(...lngs),
    east: Math.max(...lngs)
  };
}

const OVERLAY_BOUNDS_MAP = (window.config.MAP.OVERLAYS || [])
  .filter(o => !o.name.endsWith(" Label") && !o.name.endsWith(" Image"))
  .reduce((map, o) => { map[o.name] = parseBounds(o.bounds); return map; }, {});

export default {
  metaInfo: {
    title: "Welcome",
    meta: [
      {
        name: "Welcome",
        content: "welcome, landing page, capital complex, complaint tracking"
      }
    ]
  },

  components: {
    TrackComplaintStatus,
    UtilitiesPayment,
    UtilityViewerModule,
    UtilityControl,
    UtilityQuery,

    Map,
    ZoomControl,
    LayersControl,
    HomeControl,
    ScaleBarControl
  },

  data() {
    return {
      utilities: [],
      currentUtility: null,
      selected3DMapOption: null,
      selectedModule: null,
      visibleOverlayBounds: (window.config.MAP.OVERLAYS || [])
        .filter(o => o.visible && OVERLAY_BOUNDS_MAP[o.name])
        .map(o => OVERLAY_BOUNDS_MAP[o.name]),
      visibleLayerNames: (window.config.MAP.OVERLAYS || [])
        .filter(o => o.visible && OVERLAY_BOUNDS_MAP[o.name])
        .map(o => o.name)
    };
  },

  mounted() {
    this.$refs.map.getMap(map => {
      map.on("overlayadd", ({ name }) => {
        if (OVERLAY_BOUNDS_MAP[name]) {
          this.visibleOverlayBounds = [...this.visibleOverlayBounds, OVERLAY_BOUNDS_MAP[name]];
          this.visibleLayerNames = [...this.visibleLayerNames, name];
        }
      });
      map.on("overlayremove", ({ name }) => {
        if (OVERLAY_BOUNDS_MAP[name]) {
          this.visibleOverlayBounds = this.visibleOverlayBounds.filter(b => b !== OVERLAY_BOUNDS_MAP[name]);
          this.visibleLayerNames = this.visibleLayerNames.filter(n => n !== name);
        }
      });
    });
  },

  computed: {
    map3DOptions() {
      return window.config.MAP_3D_OPTIONS;
    },

    availableModules() {
      return [
        {
          id: "trackComplaint",
          title: "Track Complaint",
          description: "Track status of your complaints",
          icon: "search",
          color: "#6B5CE7"
        },
        {
          id: "utilityControl",
          title: "GIS Map",
          description: "Search and navigate utilities on map",
          icon: "map",
          color: "#D946EF"
        },
        {
          id: "utilitiesPayment",
          title: "Utility Payments",
          description: "Pay bills for utilities services",
          icon: "payment",
          color: "#06B6D4"
        },
        {
          id: "utilityQuery",
          title: "Utility Query",
          description: "Query utilities by area and type",
          icon: "manage_search",
          color: "#534AB7"
        },
      ];
    },

    getSelectedModuleTitle() {
      const module = this.availableModules.find(m => m.id === this.selectedModule);
      return module ? module.title : "";
    }
  },

  methods: {
    open3DMapInNewTab() {
      if (this.selected3DMapOption) {
        window.open(this.selected3DMapOption, "_blank", "noopener,noreferrer");
        // Reset selection to allow selecting the same option again
        this.$nextTick(() => {
          this.selected3DMapOption = null;
        });
      }
    },
    onApplyQuery({ utilities }) {
      this.utilities = utilities;
    },
    onQueryReset() {
      this.utilities = [];
      this.currentUtility = null;
    },
    onQueryResetModule() {
      this.selectedModule = null
      this.utilities = [];
      this.currentUtility = null;
    },

    onPanToAoi({ bounds }) {
      const map = this.$refs.map;
      if (map && map.map) {
        map.map.flyToBounds(bounds, { padding: [20, 20] });
      }
    },

    onPanToPoint(point) {
      this.currentUtility = point;
    },

    selectModule(moduleId) {
      this.selectedModule = moduleId;
    }
  }
};
</script>

<style lang="css" scoped>
.landing-page-container {
  padding: 0.625em 0.9375em;
  display: flex;
  width: 100%;
  height: 100%;
}

.map-gis-container {
  flex: 1;
  min-width: 0;
  margin-right: 0.625em;
}

.right-container {
  flex-direction: column;
  display: flex;
  flex-basis: 25%;
  align-items: flex-start;
}

.utility-viewer-container {
  position: relative;
  background: white;
  padding: 0.625em;
  border-radius: 0.3125em;
  flex: 1;
  min-width: 0;
}

.map-type-selector-overlay {
  position: absolute;
  top: 15px;
  left: 15px;
  width: 280px;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 4px;
  padding: 5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Right Panel Container */
.right-panel-container {
  width: 320px;
  min-width: 320px;
  max-width: 320px;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* Module Selection Styles */
.module-selection-container {
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.module-cards-grid {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  flex: 1;
  justify-content: center;
}

.module-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 14px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
}

.module-card:hover {
  border-color: #6B5CE7;
  box-shadow: 0 4px 12px rgba(107, 92, 231, 0.15);
  transform: translateY(-2px);
}

.module-card-icon {
  width: 100px;
  height: 100px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  flex-shrink: 0;
}

.module-card-content {
  flex: 1;
  min-width: 0;
}

.module-card-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;

  margin-bottom: 8px;

  .module-card-description {
    font-size: 12px;
    color: #888;
    line-height: 1.4;
  }
}

/* Module Header (Back Button) */
.module-header {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  background: white;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
  gap: 6px;
}

.back-button {
  margin: 0;
}

.module-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

/* Module Content */
.module-content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.right-panel-container {
  width: 320px;
  min-width: 320px;
  max-width: 320px;
  background: var(--v-surface-base, white);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border: 0.5px solid rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  height: 100%;
}

.module-selection-title {
  font-size: 17px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 14px;
}

.module-card:hover {
  border-color: #534AB7;
  box-shadow: 0 4px 14px rgba(83, 74, 183, 0.15);
  transform: translateY(-2px);
}
</style>
